import { api } from '@/shared/api/client'

/**
 * Communications — `/api/v1/comms`.
 *
 * ## Three shapes here carry rules the UI must not soften
 *
 * **`accepted` and `delivered` are different counts.** A provider taking a message is not a
 * handset receiving one. A screen that adds them together, or labels the first as the second,
 * has reintroduced exactly the defect the two statuses exist to prevent — and on a polling-day
 * morning the gap between them is the difference between a ward that was reminded and one that
 * was not.
 *
 * **`approvedBy` is null only while a campaign is a DRAFT.** There is no shape here for an
 * approved send nobody stands behind, because the database has no such row.
 *
 * **No endpoint returns an audience's people, and no response carries an address.** A preview
 * returns a count; a campaign returns a bounded sample of its own recipients with the phone
 * numbers left out. Anything else would be a register export wearing a preview's name.
 */

const base = '/comms'

export interface Channel {
  id: string
  channelCode: string
  providerCode: string
  displayName: string
  integrationId: string | null
  senderId: string | null
  replyTo: string | null
  /** Per BILLABLE PART, not per message. One emoji triples a campaign's cost. */
  unitCostKes: string | null
  perMinuteCap: number
  dailyCap: number | null
  isDefault: boolean
  active: boolean
  /** False when no ACTIVE integration backs the provider. Shown, never hidden. */
  configured: boolean
  lastError: string | null
}

export interface Provider {
  providerCode: string
  channelCode: string
  label: string
  configured: boolean
  requiredConfigKeys: string[]
}

export interface Template {
  id: string
  code: string
  channelCode: string
  displayName: string
  subject: string | null
  body: string
  /** Names the body uses. Every one needs a value or the campaign is refused. */
  placeholders: string[]
  footer: string | null
  languageCode: string
  active: boolean
  /** Billable parts, from the driver that knows its own encoding. */
  estimatedParts: number | null
}

export interface Campaign {
  id: string
  ref: string
  displayName: string
  purpose: string
  channelId: string
  channelName: string | null
  channelCode: string | null
  templateId: string
  templateName: string | null
  statusCode: string
  scheduledAt: string | null
  startedAt: string | null
  finishedAt: string | null
  /** Never null once the status has left DRAFT. */
  approvedBy: string | null
  approvedAt: string | null
  stoppedBy: string | null
  stoppedAt: string | null
  stoppedReason: string | null
  recipientCount: number
  /** People in the audience we may not contact. Shown beside the reach, never hidden. */
  suppressedCount: number
  estimatedCostKes: string | null
  actualCostKes: string | null
  createdAt: string
}

export interface Audience {
  id: string
  sourceCode: string
  sourceRef: string | null
  label: string
  estimatedSize: number | null
}

export interface Recipient {
  id: string
  voterId: string | null
  mobilizerId: string | null
  personName: string | null
  statusCode: string
  suppressedReason: string | null
  consentBasis: string | null
  parts: number | null
  costKes: string | null
  sentAt: string | null
  deliveredAt: string | null
  failureReason: string | null
  attempts: number
}

export interface Suppression {
  id: string
  channelCode: string
  reasonCode: string
  note: string | null
  suppressedAt: string
  suppressedBy: string | null
  releasedAt: string | null
  releasedNote: string | null
}

export interface Inbound {
  id: string
  channelCode: string
  providerCode: string
  body: string
  recipientId: string | null
  /** A STOP already honoured, without a person in the loop. */
  autoSuppressed: boolean
  handledAt: string | null
  handledBy: string | null
  receivedAt: string
}

export interface CampaignDetail {
  campaign: Campaign
  audiences: Audience[]
  template: Template | null
  /** Per-status counts. ACCEPTED and DELIVERED are separate keys, deliberately. */
  breakdown: Record<string, number>
  /** A bounded sample, never the whole list. */
  sample: Recipient[]
}

export interface Dashboard {
  campaignsDraft: number
  campaignsScheduled: number
  campaignsSending: number
  sentToday: number
  deliveredToday: number
  failedToday: number
  spentTodayKes: string | null
  suppressionsLive: number
  repliesUnhandled: number
  sendingEnabled: boolean
  quietHoursStart: number
  quietHoursEnd: number
  recent: Campaign[]
}

// ── the room ────────────────────────────────────────────────────────────────

export const getDashboard = () => api.get<Dashboard>(`${base}/dashboard`)

export const listCampaigns = () => api.get<Campaign[]>(`${base}/campaigns`)

export const getCampaign = (id: string) =>
  api.get<CampaignDetail>(`${base}/campaigns/${id}`)

export const createCampaign = (body: unknown) =>
  api.post<Campaign>(`${base}/campaigns`, body)

export const addAudience = (campaignId: string, body: unknown) =>
  api.post<Audience>(`${base}/campaigns/${campaignId}/audiences`, body)

export const removeAudience = (campaignId: string, audienceId: string) =>
  api.post<void>(`${base}/campaigns/${campaignId}/audiences/${audienceId}/remove`, {})

export const materialise = (id: string) =>
  api.post<Campaign>(`${base}/campaigns/${id}/materialise`, {})

/** The approver is the authenticated caller. There is deliberately no field for it. */
export const approveCampaign = (id: string, scheduledAt: string | null) =>
  api.post<Campaign>(`${base}/campaigns/${id}/approve`, { scheduledAt })

export const stopCampaign = (id: string, reason: string) =>
  api.post<Campaign>(`${base}/campaigns/${id}/stop`, { reason })

/** A COUNT. There is no endpoint that returns the people. */
export const previewAudience = (body: unknown) =>
  api.post<{ matches: number }>(`${base}/audiences/preview`, body)

// ── configuration ───────────────────────────────────────────────────────────

export const listChannels = () => api.get<Channel[]>(`${base}/channels`)
export const listProviders = () => api.get<Provider[]>(`${base}/providers`)
export const saveChannel = (body: unknown) => api.post<Channel>(`${base}/channels`, body)
export const updateChannel = (id: string, body: unknown) =>
  api.post<Channel>(`${base}/channels/${id}`, body)

export const listTemplates = () => api.get<Template[]>(`${base}/templates`)
export const saveTemplate = (body: unknown) => api.post<Template>(`${base}/templates`, body)
export const updateTemplate = (id: string, body: unknown) =>
  api.post<Template>(`${base}/templates/${id}`, body)

export const listSuppressions = () => api.get<Suppression[]>(`${base}/suppressions`)
export const suppress = (body: unknown) =>
  api.post<Suppression>(`${base}/suppressions`, body)
export const releaseSuppression = (id: string, note: string) =>
  api.post<Suppression>(`${base}/suppressions/${id}/release`, { note })

export const listInbound = () => api.get<Inbound[]>(`${base}/inbound`)
export const markHandled = (id: string) =>
  api.post<Inbound>(`${base}/inbound/${id}/handled`, {})
